/**
 * A burst cache implementation that deduplicates concurrent requests.
 * When multiple requests are made within a short time window,
 * they will share the same promise, preventing duplicate work.
 */
export class BurstCache<T> {
  private promise: Promise<T> | null = null;
  private timestamp = 0;
  private readonly ttl: number;

  constructor(ttl = 1000) {
    this.ttl = ttl;
  }

  private isValid(): boolean {
    if (!this.promise) return false;
    return Date.now() - this.timestamp < this.ttl;
  }

  async get(factory: () => Promise<T>): Promise<T> {
    if (this.isValid()) {
      return this.promise!;
    }

    this.promise = factory();
    this.timestamp = Date.now();

    // Clear on error
    this.promise.catch(() => {
      this.promise = null;
      this.timestamp = 0;
    });

    return this.promise;
  }

  clear(): void {
    this.promise = null;
    this.timestamp = 0;
  }
}
